import { openAccountModal } from './account-strip';
import { isCloudEnabled, getCurrentUser, signInWithGoogle } from '../shared/supabase';
import { ACCOUNT_CHANGED_EVENT } from '../shared/account';

/**
 * 學習封面 (Study Cover) — 登入後的第一張畫面,取代原本 Phaser 戰車 splash。
 *
 * 老闆 2026-06-08:封面「一看就知道是念書 / 學霸專用」的學習風,不要戰車、不要
 * 夜空紫。本檔是純 DOM 封面,走教科書 / 講義紙感美學,跟 learn-entry 既有的
 * `--le-*` token + Noto Serif TC 標題語彙一致。
 *
 * 專業教學鐵則 (CLAUDE.md / wiki she-ji-xi-tong):
 *   - 0 音效、0 mascot、0 彩光動畫。
 *   - 文案中性專業(念書 / 錯題變強 / 段考衝刺 / 學霸養成),不戲謔。
 *   - 唯一動態 = 極克制的淡入(尊重 prefers-reduced-motion)。
 *
 * 視覺:書本 / 鉛筆 / A+ 成績單 / 打勾 / 便利貼 / 檯燈暖光 的「書桌講義」意象,
 * 純 emoji + CSS 圖形,無任何遊戲戰鬥元素。
 *
 * @param container  掛載點(main.ts 傳 learnRoot)。
 * @param onStart    按「開始學習」/ Enter 時呼叫;沿用原 ef:start-game 後的路由。
 */
export function renderCover(container: HTMLElement, onStart: () => void): void {
  ensureCoverStyles();

  const root = document.createElement('div');
  root.className = 'study-cover';
  root.setAttribute('data-testid', 'study-cover');

  // 一次性 onStart 守衛 — 連點 / Enter+click 同時觸發只跑一次路由。
  let started = false;
  let stripCleanup: () => void = () => {};
  const fire = (): void => {
    if (started) return;
    started = true;
    document.removeEventListener('keydown', onKeydown);
    stripCleanup();
    onStart();
  };
  const onKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      fire();
    }
  };

  root.innerHTML = `
    <div class="study-cover__sheet" role="img"
         aria-label="念書主題:書本、鉛筆與 A+ 成績單">
      <div class="study-cover__lamp" aria-hidden="true"></div>
      <div class="study-cover__desk" aria-hidden="true">
        <span class="study-cover__motif study-cover__motif--book">📚</span>
        <span class="study-cover__motif study-cover__motif--grade">
          <span class="study-cover__grade-letter">A<sup>+</sup></span>
        </span>
        <span class="study-cover__motif study-cover__motif--pencil">✏️</span>
        <span class="study-cover__motif study-cover__motif--note">
          <span class="study-cover__check">✓</span>
        </span>
      </div>
    </div>

    <div class="study-cover__body">
      <p class="study-cover__eyebrow">三年級下學期 · 自主學習</p>
      <h1 class="study-cover__title">永恆要塞 學習堡壘</h1>
      <p class="study-cover__subtitle">錯題變強 · 段考衝刺 · 學霸養成</p>
      <p class="study-cover__lede">
        把每一題錯題,練成下一次的滿分。從課本章節到段考真卷,一步步把基礎打穩。
      </p>

      <div class="study-cover__cta" id="study-cover-cta"></div>
    </div>
  `;

  const cta = root.querySelector<HTMLElement>('#study-cover-cta')!;
  document.addEventListener('keydown', onKeydown);

  // CTA 依登入狀態分流(boss 2026-06-08「開始學習要分訪客或 Google 登入」):
  //   - 雲端未啟用 → 單純「開始學習」(向後相容,測試走這條)。
  //   - 未登入 → 「使用 Google 登入」/「以訪客身分開始」/「用 Email 登入」。
  //   - 已登入 → email +「開始學習」。
  const guestStartMarkup = (): string =>
    `<button type="button" class="study-cover__start" id="study-cover-start">開始學習</button>` +
    `<p class="study-cover__hint">按 Enter 開始</p>`;
  const loggedOutMarkup = (): string =>
    `<button type="button" class="study-cover__start" id="cover-google">使用 Google 登入</button>` +
    `<button type="button" class="study-cover__guest" id="cover-guest">以訪客身分開始 →</button>` +
    `<button type="button" class="study-cover__emaillink" id="cover-email">用 Email 登入</button>`;
  const loggedInMarkup = (email: string): string =>
    `<p class="study-cover__acct">☁️ ${escapeHtml(email)} · 已同步</p>` +
    `<button type="button" class="study-cover__start" id="study-cover-start">開始學習</button>` +
    `<p class="study-cover__hint">按 Enter 開始</p>`;

  const wireCta = (): void => {
    cta.querySelector('#study-cover-start')?.addEventListener('click', fire);
    cta.querySelector('#cover-guest')?.addEventListener('click', fire);
    cta.querySelector('#cover-google')?.addEventListener('click', () => { void signInWithGoogle(); });
    cta.querySelector('#cover-email')?.addEventListener('click', () => { openAccountModal(); });
    try { cta.querySelector<HTMLButtonElement>('#study-cover-start, #cover-google')?.focus(); } catch { /* noop */ }
  };

  const paintCta = async (): Promise<void> => {
    let email: string | null = null;
    try { email = (await getCurrentUser())?.email ?? null; } catch { email = null; }
    cta.innerHTML = email ? loggedInMarkup(email) : loggedOutMarkup();
    wireCta();
  };

  if (!isCloudEnabled()) {
    cta.innerHTML = guestStartMarkup();
    wireCta();
  } else {
    void paintCta();
    const onChange = (): void => { void paintCta(); };
    window.addEventListener(ACCOUNT_CHANGED_EVENT, onChange);
    stripCleanup = () => window.removeEventListener(ACCOUNT_CHANGED_EVENT, onChange);
  }

  container.appendChild(root);
}

/** email 顯示防禦性轉義(來自 auth provider)。 */
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c),
  );
}

/**
 * 注入封面樣式(冪等)。寫成 <style> 注入而非改 styles.css,讓封面自成一個
 * 可獨立測試的元件;token 取 learn-entry 的紙感色系,風格與課程畫面一致。
 */
function ensureCoverStyles(): void {
  if (document.getElementById('study-cover-styles')) return;
  const style = document.createElement('style');
  style.id = 'study-cover-styles';
  style.textContent = `
.study-cover {
  --sc-bg:        #f4f1ea;
  --sc-paper:     #fefcf5;
  --sc-ink:       #1a1a1a;
  --sc-text:      #3a3a36;
  --sc-sub:       #6b6b63;
  --sc-line:      #e0d9c4;
  --sc-accent:    #1f6f5c;   /* 沉穩學術綠 */
  --sc-accent-d:  #185a4a;
  --sc-link:      #1565c0;   /* 墨水藍,沿用 learn-entry --le-link */

  box-sizing: border-box;
  width: 100%;
  min-height: 100%;
  margin: 0 auto;
  padding: 40px 24px 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 28px;
  text-align: center;
  background:
    radial-gradient(120% 80% at 50% 0%, #faf8f1 0%, var(--sc-bg) 62%);
  color: var(--sc-text);
  font-family: 'Noto Sans TC', 'Microsoft JhengHei', sans-serif;
  animation: sc-fade-in 0.5s ease-out both;
}
.study-cover * { box-sizing: border-box; }

/* CTA 區:訪客 / Google 登入 / Email 分流(置中堆疊)。 */
.study-cover__cta {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-top: 18px;
}
.study-cover__cta .study-cover__start { margin-top: 0; }
.study-cover__acct {
  margin: 0 0 2px;
  font-size: 14px;
  font-weight: 600;
  color: var(--sc-accent-d);
  letter-spacing: 0.5px;
}
.study-cover__guest {
  background: transparent;
  border: none;
  color: var(--sc-accent-d);
  font-family: 'Noto Sans TC', 'Microsoft JhengHei', sans-serif;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 1px;
  cursor: pointer;
  padding: 8px 12px;
  min-height: 44px;
}
.study-cover__guest:hover { text-decoration: underline; }
.study-cover__emaillink {
  background: transparent;
  border: none;
  color: var(--sc-sub);
  font-family: 'Noto Sans TC', 'Microsoft JhengHei', sans-serif;
  font-size: 13px;
  letter-spacing: 0.5px;
  cursor: pointer;
  padding: 4px 8px;
}
.study-cover__emaillink:hover { color: var(--sc-accent-d); text-decoration: underline; }

/* ── 書桌講義主視覺 ─────────────────────────────────────────────── */
.study-cover__sheet {
  position: relative;
  width: 100%;
  max-width: 360px;
  aspect-ratio: 1 / 1;
  max-height: 300px;
  background: var(--sc-paper);
  border: 1px solid var(--sc-line);
  border-radius: 16px;
  box-shadow: 0 12px 32px rgba(60, 50, 30, 0.10);
  /* 講義橫線紙感 — 極淡,不喧賓奪主 */
  background-image:
    linear-gradient(var(--sc-paper) 0 0),
    repeating-linear-gradient(180deg, transparent 0 31px, #ece5d3 31px 32px);
  background-clip: padding-box;
  overflow: hidden;
}

/* 檯燈暖光 — 靜態徑向暈,不是彩光動畫 */
.study-cover__lamp {
  position: absolute;
  inset: 0;
  background: radial-gradient(60% 50% at 28% 22%, rgba(255, 214, 130, 0.32) 0%, transparent 70%);
  pointer-events: none;
}

.study-cover__desk {
  position: absolute;
  inset: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  place-items: center;
  padding: 18px;
}
.study-cover__motif {
  font-size: 56px;
  line-height: 1;
  filter: drop-shadow(0 4px 8px rgba(60, 50, 30, 0.12));
}
.study-cover__motif--book   { transform: rotate(-6deg); }
.study-cover__motif--pencil { transform: rotate(8deg);  }

/* A+ 成績單 — 純 CSS 卡片,不靠 emoji,凸顯「學霸」定位 */
.study-cover__motif--grade {
  width: 78px; height: 92px;
  background: #ffffff;
  border: 1px solid var(--sc-line);
  border-radius: 6px;
  box-shadow: 0 5px 12px rgba(60, 50, 30, 0.14);
  display: flex; align-items: center; justify-content: center;
  transform: rotate(5deg);
}
.study-cover__grade-letter {
  font-family: 'Noto Serif TC', serif;
  font-weight: 700;
  font-size: 44px;
  color: var(--sc-accent);
  line-height: 1;
}
.study-cover__grade-letter sup { font-size: 22px; }

/* 便利貼 + 打勾 — 「訂正完成」意象 */
.study-cover__motif--note {
  width: 78px; height: 78px;
  background: #fff6c9;
  border-radius: 4px;
  box-shadow: 0 5px 12px rgba(120, 100, 30, 0.16);
  display: flex; align-items: center; justify-content: center;
  transform: rotate(-4deg);
}
.study-cover__check {
  font-family: 'Noto Serif TC', serif;
  font-size: 46px;
  font-weight: 700;
  color: var(--sc-accent-d);
}

/* ── 文案 ───────────────────────────────────────────────────────── */
.study-cover__body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  max-width: 420px;
}
.study-cover__eyebrow {
  margin: 0;
  font-size: 13px;
  letter-spacing: 3px;
  font-weight: 600;
  color: var(--sc-accent);
  text-transform: none;
}
.study-cover__title {
  margin: 0;
  font-family: 'Noto Serif TC', serif;
  font-weight: 700;
  font-size: 34px;
  line-height: 1.25;
  color: var(--sc-ink);
  letter-spacing: 1px;
}
.study-cover__subtitle {
  margin: 2px 0 0;
  font-family: 'Noto Serif TC', serif;
  font-size: 18px;
  font-weight: 500;
  color: var(--sc-text);
  letter-spacing: 0.5px;
}
.study-cover__lede {
  margin: 6px 0 0;
  font-size: 15px;
  line-height: 1.8;
  color: var(--sc-sub);
}

/* ── 主按鈕 ─────────────────────────────────────────────────────── */
.study-cover__start {
  margin-top: 18px;
  min-height: 52px;
  padding: 14px 56px;
  border: none;
  border-radius: 999px;
  background: var(--sc-accent);
  color: #ffffff;
  font-family: 'Noto Sans TC', 'Microsoft JhengHei', sans-serif;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 2px;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(31, 111, 92, 0.28);
  transition: background 0.18s ease, transform 0.08s ease, box-shadow 0.18s ease;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}
.study-cover__start:hover {
  background: var(--sc-accent-d);
  transform: translateY(-2px);
  box-shadow: 0 12px 26px rgba(31, 111, 92, 0.34);
}
.study-cover__start:active { transform: scale(0.97); }
.study-cover__start:focus-visible {
  outline: 3px solid rgba(31, 111, 92, 0.4);
  outline-offset: 3px;
}
.study-cover__hint {
  margin: 12px 0 0;
  font-size: 13px;
  color: var(--sc-sub);
  letter-spacing: 1px;
}

/* ── 響應式:手機優先,放大主視覺與標題 ───────────────────────────── */
@media (min-width: 640px) {
  .study-cover { gap: 34px; padding: 56px 24px; }
  .study-cover__sheet { max-width: 420px; max-height: 340px; }
  .study-cover__motif { font-size: 64px; }
  .study-cover__title { font-size: 42px; }
  .study-cover__subtitle { font-size: 20px; }
}

@keyframes sc-fade-in {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* 尊重「減少動態」偏好:停用淡入。 */
@media (prefers-reduced-motion: reduce) {
  .study-cover { animation: none; }
}
`;
  document.head.appendChild(style);
}
